import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  Eye,
  Download,
  Star,
  Grid3x3,
  List,
  ChevronDown,
  Loader,
  Globe,
  Smartphone,
  ShoppingCart,
  Briefcase,
  Camera,
  Heart,
  Zap,
  Users,
  BookOpen,
  Coffee
} from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const TemplateBrowser = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    categories: [],
    collections: [],
    totalCount: 0,
    hasMore: false
  });

  const categoryIcons = {
    'e-commerce': ShoppingCart,
    'business': Briefcase,
    'portfolio': Camera,
    'blog-news': BookOpen,
    'restaurant': Coffee,
    'real-estate': Globe,
    'healthcare': Heart,
    'education': BookOpen,
    'events': Heart,
    'technology': Zap,
    'lead-capture': Users,
    'event-invitation': Heart,
    'general': Globe
  };

  useEffect(() => {
    loadTemplates();
  }, [searchQuery, selectedCategory, selectedCollection]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedCollection) params.append('collection', selectedCollection);
      params.append('limit', '50');

      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/templates?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        setFilters({
          categories: data.categories || [],
          collections: data.collections || [],
          totalCount: data.total_count || 0,
          hasMore: data.has_more || false
        });
      } else {
        toast.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Error loading templates');
    } finally {
      setLoading(false);
    }
  };

  const createPageFromTemplate = async (template) => {
    const pageName = prompt(`Create page from "${template.name}"\n\nEnter page name:`);
    if (!pageName) return;

    setCreating(template.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/member/pages/from-template`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_id: template.id,
          page_name: pageName
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Page "${pageName}" created successfully!`);
        // You could redirect to page editor here
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create page');
      }
    } catch (error) {
      console.error('Error creating page:', error);
      toast.error('Error creating page from template');
    } finally {
      setCreating(null);
    }
  };

  const getCategoryIcon = (category) => {
    const IconComponent = categoryIcons[category] || Globe;
    return <IconComponent className="w-4 h-4" />;
  };

  const formatCollectionName = (collection) => {
    return collection.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const getTemplateTypeColor = (type) => {
    switch(type) {
      case 'classic_web': return 'bg-blue-100 text-blue-800';
      case 'lead_funnel': return 'bg-green-100 text-green-800';
      case 'event_invite': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Template Library</h1>
          <p className="text-gray-600 mt-2">
            Choose from {filters.totalCount} professional templates to create your pages
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid3x3 className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="flex space-x-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {filters.categories.map(category => (
              <option key={category} value={category}>
                {category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
          
          <select
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Collections</option>
            {filters.collections.map(collection => (
              <option key={collection} value={collection}>
                {formatCollectionName(collection)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Templates Grid/List */}
      {templates.length === 0 ? (
        <div className="text-center py-12">
          <Globe className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          : "space-y-4"
        }>
          {templates.map((template) => (
            <Card key={template.id} className={`group hover:shadow-lg transition-shadow ${
              viewMode === 'list' ? 'flex flex-row' : ''
            }`}>
              {viewMode === 'grid' ? (
                <>
                  <div className="aspect-video bg-gray-100 rounded-t-lg relative overflow-hidden">
                    {template.preview_images && template.preview_images.length > 0 ? (
                      <img 
                        src={`/templates/${template.metadata?.template_dir}/${template.preview_images[0]}`}
                        alt={template.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {getCategoryIcon(template.category)}
                      </div>
                    )}
                    
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Button
                        size="sm"
                        onClick={() => createPageFromTemplate(template)}
                        disabled={creating === template.id}
                        className="bg-white text-gray-900 hover:bg-gray-100"
                      >
                        {creating === template.id ? (
                          <Loader className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Use Template
                      </Button>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-gray-900 truncate">{template.name}</h3>
                      {template.is_mobile_responsive && (
                        <Smartphone className="w-4 h-4 text-green-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {template.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(template.category)}
                        <span className="text-xs text-gray-500">
                          {template.category.replace('-', ' ')}
                        </span>
                      </div>
                      
                      <span className={`text-xs px-2 py-1 rounded-full ${getTemplateTypeColor(template.type)}`}>
                        {template.collection_name}
                      </span>
                    </div>
                    
                    {template.tags && template.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </>
              ) : (
                // List view
                <div className="flex w-full">
                  <div className="w-32 h-24 bg-gray-100 rounded-l-lg flex-shrink-0 flex items-center justify-center">
                    {getCategoryIcon(template.category)}
                  </div>
                  
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-medium text-gray-900">{template.name}</h3>
                          {template.is_mobile_responsive && (
                            <Smartphone className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>{template.category.replace('-', ' ')}</span>
                          <span>•</span>
                          <span>{template.collection_name}</span>
                          {template.usage_count > 0 && (
                            <>
                              <span>•</span>
                              <span>{template.usage_count} uses</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => createPageFromTemplate(template)}
                        disabled={creating === template.id}
                        size="sm"
                      >
                        {creating === template.id ? (
                          <Loader className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Use Template
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Load More */}
      {filters.hasMore && (
        <div className="text-center">
          <Button variant="outline" onClick={() => {/* Load more implementation */}}>
            Load More Templates
          </Button>
        </div>
      )}
    </div>
  );
};

export default TemplateBrowser;